import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import { LoginService, SessionCookieService } from '@docpath/linces-angular-components/common';
import { DpMainConfig, DpMainViewModule, DpMenuItem, NavAction, User } from '@docpath/webcomponents';
import { faCog, faGauge, faSignal, faTableColumns } from '@fortawesome/free-solid-svg-icons';
import { environment } from 'src/environments/environment';

import { Constants } from '../constants/constants';
import { RoutingPath } from '../constants/routing-path';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  standalone: true,
  imports: [DpMainViewModule]
})
export class MainComponent implements OnInit {

  openMenu = false;

  mainApp: DpMenuItem = new DpMenuItem();
  bottomLinks: NavAction[] = [];
  topLinks: NavAction[] = [];
  userApps?: DpMenuItem[] = [];
  showLoading = false;
  user!: User;

  mainView: DpMainConfig;


  private readonly router = inject(Router);
  private readonly loginService = inject(LoginService);
  private readonly sessionCookieService = inject(SessionCookieService);
  private readonly titleService = inject(Title);

  constructor() {
    this.titleService.setTitle(`DocPath - ${environment.appName}`);

    this.mainView = {
      mainApp: this.mainApp,
      bottomLinks: this.bottomLinks,
      showUserApps: true,
      topLinks: this.topLinks,
      user: this.user,
      userApps: this.userApps,
      titleVisible: true
    };
  }

  ngOnInit(): void {

    this.initData();

    this.router.events.subscribe(event => {
      if (this.isValidRouterEvent(event)) {

        this.initData();
      }
    });

  }

  private isValidRouterEvent(event: unknown): boolean {
    if (event instanceof NavigationEnd) {
      return true;
    }

    return event instanceof Scroll && event.routerEvent instanceof NavigationEnd;
  }

  private initData(): void {

    this.initTopLinks();
    this.initUser();
    this.initNavActions();
    this.initMainApp();
    this.initUserApps();
    this.loadUserApps();

  }

  logout(): void {
    this.loginService.logout();
  }

  initUserApps() {
    const user = this.sessionCookieService.getSessionCookie()?.user;

    if (user?.role === Constants.ROLE_LOCAL) {
      this.userApps = undefined;
    }
  }

  loadUserApps(): void {
    const user = this.sessionCookieService.getSessionCookie()?.user;

    this.showLoading = true;

    if (user?.role !== Constants.ROLE_LOCAL) {
      this.loginService.getUserApplications().subscribe(apps => {
        this.userApps = apps.map(app => new DpMenuItem(app.type, app.url, app.type));
        this.mainView.userApps = this.userApps;
        this.showLoading = false;
      });
    } else {
      this.userApps = undefined;
    }
  }

  private initNavActions(): void {
    this.bottomLinks = [];
    const navActionsList = [
      new NavAction({
        url: `/${RoutingPath.PATH_CONFIGURATION}`,
        icon: { name: faCog },
        label: 'app.configuration.label',
        tooltip: 'app.configuration.tooltip'
      })
    ];

    this.loadUserActions(navActionsList, false);
    this.mainView.bottomLinks = this.bottomLinks;
  }

  private initTopLinks(): void {
    this.topLinks = [];
    const navActionsList = [
      new NavAction({
        url: `/${RoutingPath.PATH_HOME}`,
        icon: { name: faTableColumns },
        label: 'app.home.label',
        tooltip: 'app.home.tooltip'
      }),
      new NavAction({
        url: `/${RoutingPath.PATH_STATISTICS}`,
        icon: { name: faSignal },
        label: 'app.statistics.label',
        tooltip: 'app.statistics.tooltip'
      }),
      new NavAction({
        url: `/${RoutingPath.PATH_MONITORING}`,
        icon: { name: faGauge },
        label: 'app.monitoring.label',
        tooltip: 'app.monitoring.tooltip'
      })
    ];

    this.loadUserActions(navActionsList, true);
    this.mainView.topLinks = this.topLinks;
  }

  private loadUserActions(navActionsList: NavAction[], isTopAction: boolean): void {
    const paths = this.sessionCookieService.getSessionCookie()?.authorization.paths;

    navActionsList.forEach(navAction => {
      if (paths?.includes(navAction.url)) {
        if (isTopAction) {
          this.topLinks.push(navAction);
        } else {
          this.bottomLinks.push(navAction);
        }
      }
    });
  }

  private initUser(): void {
    const user = this.sessionCookieService.getSessionCookie()?.user;

    if (user) {
      this.user = new User(user.name, '', user.fullName !== '' ? user.fullName : user.name, user.email);
      this.mainView.user = this.user;
    }
  }

  private initMainApp(): void {
    this.mainApp = new DpMenuItem(environment.appName, environment.baseUrl, 'assets/images/logo.svg');
    this.mainView.mainApp = this.mainApp;
  }
}
